import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

interface IProps extends DialogProps {
  title: string;
  handleClose: VoidFunction;
  hideCloseIcon?: boolean;
  variant?: string;
}

const CustomModal: React.FC<IProps> = ({
  open,
  handleClose,
  hideCloseIcon,
  title,
  children,
  variant,
  ...props
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="movable-dialog-title"
      aria-describedby="scroll-dialog-description"
      {...props}
      PaperProps={{
        sx: {
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.1s',
        },
      }}
    >
      <DialogTitle
        id="movable-dialog-title"
        className="modal-header"
        sx={{
          borderRadius: props.fullScreen ? 0 : '4px 4px 0 0',
          paddingY: '4px',
          minHeight: '40px',
          cursor: 'move',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography
            justifyItems="center"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            {variant === 'warning' && <WarningAmberRoundedIcon />}
            {title}
          </Typography>
          {!hideCloseIcon && (
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
};

export default CustomModal;
